import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { lazy } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeftOutlined } from '@ant-design/icons'

import { useStore } from 'store/useStore'
import { ContainerLK } from 'sections/LKSection/shared/components/ContainerLK/ContainerLK'
import { Margin, Padding } from 'components/ui'
import { Button, Flex, Skeleton, Title } from 'components/uiKit'
import { useAsyncEffect } from 'utils/hooks/useAsyncEffect'
import { LKRoutesEnum } from 'shared/constants/routes'
import { SEARCH_PARAMS } from 'shared/constants/values'

import { OrderKeys } from '../../data'
import { OrderStatus } from '../../types'
import { OrderItemForm } from './OrderItemForm/OrderItemForm'
import { OrderItemStatusView } from './OrderItemStatusView/OrderItemStatusView'

import s from './OrderItemPage.module.scss'

type OrderItemPageProps = {
  hasErrorWithPayment?: boolean
}

const OrderItemPage = observer(({ hasErrorWithPayment }: OrderItemPageProps) => {
  const { viewOrderUiStore: uiStore } = useStore()

  const { orderId } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const order = uiStore.selectedOrder

  const searchParams = new URLSearchParams(location.search)
  const pageFromUrl = searchParams.get(SEARCH_PARAMS.page)
  const pageSizeFromUrl = searchParams.get(SEARCH_PARAMS.pageSize)

  useEffect(() => {
    if (pageFromUrl && pageSizeFromUrl) {
      uiStore.setPage(+pageFromUrl)
      uiStore.setPageSize(+pageSizeFromUrl)
    }
  }, [pageFromUrl, pageSizeFromUrl, uiStore])

  useAsyncEffect(async () => {
    if (orderId) {
      if (uiStore.orderList.length === 0) {
        await uiStore.getOrderList()
      }

      uiStore.setSelectedOrder(uiStore.getOrder(+orderId))

      if (uiStore.selectedOrder?.id && uiStore.selectedOrder.status === OrderStatus.waitingPayment) {
        uiStore.checkOrderPayment(+uiStore.selectedOrder.id)
      }
    }
  }, [orderId])

  useEffect(() => {
    return () => {
      uiStore.reset()
    }
  }, [])

  const backButtonHandler = () => {
    navigate(LKRoutesEnum.orders)
  }

  const getKey = (key: string) => `${OrderKeys.orderItemPage}.${key}`

  return (
    <ContainerLK>
      <Padding py={32} className={s.OrderItemPage}>
        <Flex vertical className={s.wrap}>
          <Margin mb={32}>
            <Flex gap={16} align="center">
              <Button shape="circle" icon={<ArrowLeftOutlined />} onClick={backButtonHandler} />

              <Title>{t(getKey('Заявка №'), { count: orderId ? +orderId : 0 })}</Title>
            </Flex>
          </Margin>

          <Skeleton loading={uiStore.loadingOrderList}>
            {order ? (
              <Flex vertical>
                <OrderItemStatusView
                  order={order}
                  paymentStatus={uiStore.orderPaymentStatus}
                  hasErrorWithPayment={hasErrorWithPayment}
                />

                <OrderItemForm order={order} />
              </Flex>
            ) : (
              <Flex>
                <Title type="secondary" level={4}>
                  {t(getKey('Информации по данной заявке нет'))}
                </Title>
              </Flex>
            )}
          </Skeleton>
        </Flex>
      </Padding>
    </ContainerLK>
  )
})

export default OrderItemPage

export const OrderItemPageAsync = lazy(() => import('./OrderItemPage'))
